import React, { useEffect, useMemo, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { getStoredUser, getToken, setAuth } from "@/lib/auth";

export default function ProfileUpdateDialog({ trigger, onUpdated, testIdPrefix = "profile-update" }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [initialName, setInitialName] = useState("");
  const [initialEmail, setInitialEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const currentUser = getStoredUser();
    const nextName = currentUser?.name || "";
    const nextEmail = currentUser?.email || "";
    setName(nextName);
    setEmail(nextEmail);
    setInitialName(nextName);
    setInitialEmail(nextEmail);
  }, [open]);

  const hasChanges = useMemo(() => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    return trimmedName !== initialName || trimmedEmail !== initialEmail;
  }, [email, initialEmail, initialName, name]);

  const handleSave = async () => {
    if (!hasChanges) return;
    const payload = {};
    if (name.trim()) payload.name = name.trim();
    if (email.trim()) payload.email = email.trim();

    setSaving(true);
    try {
      const res = await api.put("/auth/me", payload);
      const token = getToken();
      if (token) {
        setAuth(token, res.data);
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("user-profile-updated", { detail: res.data }));
      }
      toast({ title: "Profile updated" });
      onUpdated?.(res.data);
      setOpen(false);
    } catch (e) {
      toast({
        title: "Update failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="glass max-w-lg border-white/10" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-slate-100">Update Profile</DialogTitle>
          <DialogDescription className="text-slate-200/70">
            Update your display name and login email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200/80" htmlFor={`${testIdPrefix}-name`}>
              Name
            </Label>
            <Input
              id={`${testIdPrefix}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 text-slate-100"
              data-testid={`${testIdPrefix}-name-input`}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200/80" htmlFor={`${testIdPrefix}-email`}>
              Email
            </Label>
            <Input
              id={`${testIdPrefix}-email`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              className="bg-white/5 text-slate-100"
              data-testid={`${testIdPrefix}-email-input`}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            className="rounded-full bg-indigo-500/80 text-white hover:bg-indigo-500"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            data-testid={`${testIdPrefix}-save-button`}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
